import type { AttentionItem, DueItem, TodayCall, TodayOverview } from '../types';
import { addDays, dayDiff, parseDate, startOfToday } from '../lib/dates';
import { getFacility } from './facilityService';
import { loadClinic } from './store';
import { getDailyStats } from './statsService';
import { milestoneItems, sourceOf } from './milestones';
import { consentStatusOf } from './consent';

export async function getTodayOverview(facilityId: string): Promise<TodayOverview> {
  const [facility, clinic, stats] = await Promise.all([getFacility(facilityId), loadClinic(facilityId), getDailyStats(facilityId, 1)]);
  const { babies, calls } = clinic;
  // Leave out anything whose mother or baby record is missing, so one bad record can't break the page.
  const mothers = clinic.mothers;
  const known = new Set(mothers.map((m) => m.id));
  const babyMother = new Map(babies.map((b) => [b.id, b.motherId]));
  const doses = clinic.doses.filter((d) => known.has(babyMother.get(d.babyId) ?? ''));
  const flags = clinic.flags.filter((f) => known.has(f.motherId));
  const callsKnown = calls.filter((c) => known.has(c.motherId));
  const today = startOfToday();
  const motherById = new Map(mothers.map((m) => [m.id, m]));
  const babyByMother = new Map(babies.map((b) => [b.motherId, b]));
  const babyById = new Map(babies.map((b) => [b.id, b]));
  const withMother = (motherId: string) => {
    const mother = motherById.get(motherId)!;
    return { mother, baby: babyByMother.get(motherId) };
  };

  const flagItems: AttentionItem[] = flags
    .filter((f) => f.status !== 'resolved')
    .sort((a, b) => (a.urgency === b.urgency ? b.raisedAt.localeCompare(a.raisedAt) : a.urgency === 'urgent' ? -1 : 1))
    .map((f) => ({ id: f.id, kind: 'flag', ...withMother(f.motherId), urgency: f.urgency, reason: f.reason, reasonHi: f.reasonHi, assignedTo: f.assignedTo, acknowledgedAt: f.acknowledgedAt, acknowledgedBy: f.acknowledgedBy, at: f.raisedAt }));

  const missedItems: AttentionItem[] = doses
    .filter((d) => d.status === 'missed' || (d.status !== 'attended' && dayDiff(parseDate(d.dueDate), today) > 3))
    .map((d) => ({ id: d.id, kind: 'missed_dose', ...withMother(babyById.get(d.babyId)!.motherId), reason: d.name, at: d.dueDate }));

  const todays = callsKnown.filter((c) => dayDiff(today, parseDate(c.startedAt)) === 0);
  const failedItems: AttentionItem[] = todays
    .filter((c) => c.outcome === 'failed')
    .map((c) => ({ id: c.id, kind: 'failed_call', ...withMother(c.motherId), reason: c.summary, reasonHi: c.summaryHi, at: c.startedAt }));

  const latestByMother = new Map<string, (typeof calls)[number]>();
  callsKnown.forEach((c) => {
    const prev = latestByMother.get(c.motherId);
    if (!prev || prev.startedAt < c.startedAt) latestByMother.set(c.motherId, c);
  });
  const retryItems: AttentionItem[] = Array.from(latestByMother.values())
    .filter((c) => ['no_answer', 'failed'].includes(c.outcome) && dayDiff(parseDate(c.startedAt), today) >= 1)
    .filter((c) => { const m = motherById.get(c.motherId); return !!m && consentStatusOf(m) !== 'no'; })
    .map((c) => ({ id: `retry-${c.id}`, kind: 'retry_due', ...withMother(c.motherId), at: c.startedAt }));

  const todaysCalls: TodayCall[] = todays
    .sort((a, b) => a.startedAt.localeCompare(b.startedAt))
    .map((c) => ({ call: c, ...withMother(c.motherId) }));

  const weekEnd = addDays(today, 6);
  const inWeek = (d: Date) => dayDiff(today, d) >= 0 && dayDiff(d, weekEnd) >= 0;
  const doseDue: DueItem[] = doses
    .filter((d) => (d.status !== 'attended' && parseDate(d.dueDate) < today) || inWeek(parseDate(d.dueDate)))
    .map((d) => ({ id: d.id, kind: 'dose', ...withMother(babyById.get(d.babyId)!.motherId), name: d.name, date: d.dueDate, status: d.status }));
  const milestones: DueItem[] = mothers.flatMap((m) => milestoneItems(m, false, sourceOf(clinic))).filter((i) => inWeek(parseDate(i.date)));

  return {
    attention: [...flagItems, ...missedItems, ...failedItems, ...retryItems],
    todaysCalls,
    dueThisWeek: [...doseDue, ...milestones].sort((a, b) => a.date.localeCompare(b.date)),
    onTimeRate: stats[stats.length - 1]?.onTimeRate ?? null,
    baselineOnTimeRate: facility.baselineOnTimeRate,
    consentPending: mothers.filter((m) => consentStatusOf(m) === 'pending').length,
  };
}
